import { useCallback, useMemo, useRef, useState } from 'react'
import { FiTrash } from 'react-icons/fi'

import { useQuery } from '@tanstack/react-query'
import { Scope } from '@unform/core'
import { t } from 'i18next'
import { debounce } from 'lodash'

import { Button } from '~/components/Buttons'
import ReactSelect from '~/components/unform/ReactSelect'
import useQueryParams from '~/hooks/useQuery'
import { listAttributesService } from '~/modules/retailMedia/services/audience/attributes'

import { fieldKeyOptions } from './fieldKeyOptions'

const translateFeatureValue = (value: string) => {
  const translations: Record<string, string> = {
    F: t('rm:audience.create.rules.gender.f'),
    M: t('rm:audience.create.rules.gender.m')
  }

  return translations[value] || value
}

/**
 * Compomente que permite a seleção de atributos para a regra de segmentação.
 */

const AttributeSelect: React.FC<AttributeSelectProps> = ({
  rule,
  selectedKeys,
  onChange,
  onDelete
}) => {
  const [searchLoading, setSearchLoading] = useState(false)
  const [query, setQuery] = useState(null)

  const publisher_id = useQueryParams().get('rmid') as PublisherId

  const isActive = useMemo(() => !!rule.feature_key, [rule.feature_key])

  const isSearchable = useMemo(
    () =>
      fieldKeyOptions.find(item => item.value === rule.feature_key)
        ?.isSearchable,
    [rule.feature_key]
  )

  /**
   * <Handle options>
   * Check for the field key select.
   */
  const options = useMemo(
    () =>
      fieldKeyOptions.map(item => ({
        ...item,
        disabled: selectedKeys.includes(item.value)
      })),
    [selectedKeys]
  )
  /*** </Handle options> */

  /**
   * <Handle attibutes>
   */
  const queryKey = useMemo(
    () => [
      'audiences-attibutes',
      { featureKey: rule.feature_key },
      query,
      publisher_id
    ],
    [rule.feature_key, query, publisher_id]
  )
  const params = useMemo(
    () => ({
      feature_key: rule.feature_key,
      query,
      publisher_id,
      quantity: 100
    }),
    [rule.feature_key, query, publisher_id]
  )

  const { data, isPending, isError } = useQuery({
    enabled: isActive,
    queryKey,
    placeholderData: previousData => previousData,

    queryFn: async () => {
      setSearchLoading(false)

      const response = await listAttributesService(params)

      return response.data
    }
  })
  /*** </Handle attibutes> */

  /**
   * <Handle attibutes options>
   */

  const attributeOptions = useMemo(() => {
    if (!data?.data) return null

    return data.data.map(item => ({
      label: translateFeatureValue(item.feature_value),
      value: item.feature_value
    }))
  }, [data])

  /**
   * Handle search term
   */

  /**
   * executeDebounceFunc()
   *
   * A função executeDebounceFunc serve para certificar que sempre estaremos
   * executando a versão atualizada da queryString que se mantém
   * atualizada através do useCallback.
   * */

  type DebounceFunction = (...args: unknown[]) => void

  const executeDebounceFunc: DebounceFunction = (func: () => void): void =>
    func()

  /**
   * Handling a delay to not overlap calls
   * Executa a função após 1 segundo sem interação com o componente
   */
  const handleDebounce = useRef(debounce(executeDebounceFunc, 1000)).current

  /**
   * Handling the search term
   */
  const handleQueryTerm = useCallback(
    (term: string) => {
      setSearchLoading(!!term)

      if (term !== '') {
        handleDebounce(() => setQuery(term))
      }
    },
    [handleDebounce]
  )

  /**
   * Placeholder
   */

  const formattedPlaceholder = useMemo(() => {
    switch (true) {
      case isPending && isActive:
        return t('rm:audience.create.rules.fieldValue.loading')
      case isError:
        return t('rm:audience.create.rules.fieldValue.error')
      case Boolean(query):
        return t('rm:audience.create.rules.fieldValue.filterBy', { query })
      default:
        return t('rm:audience.create.rules.fieldValue.placeholder')
    }
  }, [isPending, isActive, isError, query])

  /**
   * Handle on change when only on options on result
   * If there is only one option available, query should be set to null after selection
   */
  const hangleFeatureValuesChange = useCallback(() => {
    if (attributeOptions?.length === 1) {
      setQuery(null)
    }
  }, [attributeOptions?.length])

  return (
    <Scope path={rule.id}>
      <fieldset className="d-flex gap-1">
        <div style={{ minWidth: '8rem' }}>
          <ReactSelect
            placeholder={t('rm:audience.create.rules.fieldKey.placeholder')}
            options={options}
            name="feature_key"
            isOptionDisabled={(option: AttributeFieldKeyOption) =>
              option.disabled
            }
            isClearable={false}
            defaultValue={options.find(item => item.value === rule.feature_key)}
            onChange={(data: AttributeFieldKeyOption) =>
              onChange({ id: rule.id, feature_key: data?.value })
            }
          />
        </div>

        <div className="flex-fill">
          <ReactSelect
            placeholder={formattedPlaceholder}
            options={attributeOptions}
            name="feature_values"
            isMulti
            closeMenuOnSelect={false}
            onChange={hangleFeatureValuesChange}
            onBlur={() => setQuery(null)}
            {...(isSearchable && { onInputChange: handleQueryTerm })}
            loading={isActive && (isPending || searchLoading)}
            loadingMessage={() =>
              t('rm:audience.create.rules.fieldValue.loading')
            }
            noOptionsMessage={() =>
              query ? (
                <Button
                  template="transparent"
                  onClick={() => setQuery(null)}
                  text={t(
                    'rm:audience.create.rules.fieldValue.noOptionsWithQuery',
                    {
                      query
                    }
                  )}
                />
              ) : (
                t('rm:audience.create.rules.fieldValue.noOptions')
              )
            }
            allowCreateWhileLoading
          />
        </div>

        {onDelete && (
          <Button
            text={<FiTrash />}
            template="secondary"
            onClick={() => onDelete(rule.id)}
            customWidth="auto"
          />
        )}
      </fieldset>
    </Scope>
  )
}

export default AttributeSelect
